import {
  defaultViewHref,
  hrefOf,
  isBareListUrl,
  listViewPathOf,
  viewForBareVisit,
  type ListViewPath,
} from "_shared/_views/list-pages/current-table-view";
import { useGetTableColumnPreferencesQuery } from "_shared/_views/tables/_api/table-column-preferences-api";
import {
  fromTableViewStateJSON,
  useSaveCurrentTableViewMutation,
  type TableViewState,
} from "_shared/_views/tables/_api/table-views-api";
import type {
  NamedTableViewJSON,
  TableColumnPreferenceJSON,
} from "_shared/_views/tables/_schemas/table-column-preferences-schema";
import { TableColumnPreferencesContext } from "_shared/_views/tables/table-column-preferences-context";
import { useCallback, useContext, useEffect, useMemo, useRef } from "react";
import { useLocation, useNavigate } from "react-router";
import { useSession } from "users/__session/session-context";

export type CurrentTableViewOptions = Readonly<{
  /** The table's key on the account, as the Show/Hide columns choice is kept under. */
  tableKey: string;
  /**
   * The list's own path, which is its Default view: `/proposals`. A page declares its Default view
   * once, as this path and the defaults its `TableViewSpec` gives the chips, sort and page size.
   */
  basePath: string;
  /**
   * Where a bare visit goes when the reader keeps no view, for a page whose Default view is not its
   * bare path: `/contracts` opens `/contracts/templates`. Leave it out when the bare path is the view.
   */
  defaultHref?: string;
  /**
   * The stored view with the parts this page no longer has taken out -- a chip that was removed, a
   * tab the reader may no longer open -- or undefined when none of it can be shown. A column the
   * table no longer has needs nothing here: MRT looks up only the columns it has.
   */
  keepWhatStillExists?: (view: ListViewPath) => ListViewPath | undefined;
  /**
   * Neither opens nor keeps a stored view, for a reader the page shows one fixed view (restricted
   * onboarding). A bare visit still goes to `defaultHref`.
   */
  disabled?: boolean;
}>;

/**
 * What the history entry of a view the page opened itself records: the saved view it is, or null for
 * the Default view. A bare visit that opened the starred view has not saved anything, so the Views
 * menu reads the view's name from here rather than from the account's current view.
 */
const OPENED_VIEW_STATE = "openedTableViewId";

export function openedViewIdOf(state: unknown): string | null | undefined {
  if (typeof state !== "object" || state === null || !(OPENED_VIEW_STATE in state)) return undefined;
  const id = (state as Record<string, unknown>)[OPENED_VIEW_STATE];
  return typeof id === "string" ? id : null;
}

/**
 * `viewId`, while the account still has that saved view; null -- the Default view -- once it was
 * deleted, so a history entry naming it does not send the next change to a view that is gone, which
 * the account refuses (#6717 review). `views` is undefined while the account has not answered.
 */
export function stillSavedViewId(
  viewId: string | null | undefined,
  views: readonly Pick<NamedTableViewJSON, "id">[] | undefined,
): string | null | undefined {
  if (viewId === undefined || viewId === null || views === undefined) return viewId;
  return views.some((view) => view.id === viewId) ? viewId : null;
}

/** The Default view's columns: none chosen, so every column shows as the page defines it. */
const DEFAULT_VIEW_COLUMNS = { columnVisibility: {}, columnSizing: {}, columnOrder: [] } as const;

const NO_VIEWS: readonly NamedTableViewJSON[] = [];

/**
 * The view of a list the reader left it in, kept on their account so it follows them to another
 * browser (#6714), and built on the list's URL state (#6712) rather than beside it.
 *
 * - **A bare visit** -- the side nav's `/proposals` -- opens the reader's starred view, else the view
 *   they left the list in, else the page's Default view. The starred view opens columns and all, and
 *   is kept as the current view, as choosing it from the Views menu does.
 * - **A URL that names a view** is shown exactly, and is never overridden or saved: an assistant
 *   `listUrl`, a dashboard card, an email, a shared link. `?view=default` names the Default view.
 * - **A change the reader makes** is saved. The page calls `remember` with the URL it is about to
 *   go to, from the chip, tab, filter or sort handler; `useTableViewParams` does it for the setters it
 *   hands out when given `onReaderChange`. Arriving saves nothing else.
 */
export function useCurrentTableView(options: CurrentTableViewOptions) {
  const { tableKey, basePath, defaultHref, keepWhatStillExists, disabled } = options;
  const session = useSession();
  const location = useLocation();
  const navigate = useNavigate();
  const isLoggedIn = Boolean(session?.isLoggedIn);
  const query = useGetTableColumnPreferencesQuery({ session }, { skip: !isLoggedIn || Boolean(disabled) });
  const table = query.data?.tables.find((each) => each.tableKey === tableKey);
  // A failed answer opens the Default view rather than waiting for one that is not coming.
  const answered = query.isSuccess || query.isError;
  const [saveCurrentView] = useSaveCurrentTableViewMutation();

  // The URL the page itself is about to go to: its own change, not an arrival. And the last
  // location dealt with, so an answer arriving later does not open a view over one the reader chose.
  const ownHref = useRef<string | undefined>(undefined);
  const handledKey = useRef<string | undefined>(undefined);
  // The saved view a bare visit opened, when it was the starred one rather than the view the reader
  // left: the next change starts from it, not from the view the current view had started from.
  const openedViewId = useRef<string | null | undefined>(undefined);
  const isBare = isBareListUrl(basePath, location.pathname, location.search);
  const savedViews = query.data ? (table?.views ?? NO_VIEWS) : undefined;
  const openedFromHistory = stillSavedViewId(openedViewIdOf(location.state), savedViews);
  const columnPreferences = useContext(TableColumnPreferencesContext);
  const openedView = columnPreferences?.openedView;

  useEffect(() => {
    if (handledKey.current === location.key) return;
    const here = { pathname: location.pathname, search: location.search };
    if (ownHref.current !== undefined && isSameHref(ownHref.current, here)) {
      ownHref.current = undefined;
      handledKey.current = location.key;
      return;
    }
    const keepsViews = isLoggedIn && !disabled;
    // A history entry that names a view is that view, bare URL or not: a view of columns alone is
    // `/proposals`, and Back to it must not open the starred view instead (#6717 review).
    if (!isBare || (openedFromHistory !== undefined && keepsViews)) {
      if (openedFromHistory !== undefined && keepsViews && !answered) return;
      handledKey.current = location.key;
      // Back or Forward to the entry of a view opened earlier: its tab and chips come from the URL,
      // but the columns would stay those of the view opened since, and Save changes would write them
      // into this one (#6717 review). So it is kept as the current view again, columns and all.
      if (openedFromHistory === undefined || !keepsViews || !session) return;
      if (openedFromHistory === (table?.currentView?.viewId ?? null)) return;
      const reopened = table?.views?.find((view) => view.id === openedFromHistory);
      const columns = reopened ? fromTableViewStateJSON(reopened.state) : DEFAULT_VIEW_COLUMNS;
      const state: TableViewState = { ...columns, ...listViewPathOf(location.pathname, location.search) };
      save(saveCurrentView, { session, tableKey, viewId: openedFromHistory, state });
      openedView?.(tableKey);
      return;
    }
    if (keepsViews && !answered) return;
    handledKey.current = location.key;

    const stored = keepsViews ? viewForBareVisit(table) : undefined;
    const kept = stored && keepWhatStillExists ? keepWhatStillExists(stored) : stored;
    // A stored view the page can no longer show opens the Default view, which is no saved view.
    openedViewId.current = stored === undefined ? undefined : kept ? stored.viewId : null;
    // Its tab and chips alone would show the starred view under the columns of the view the reader
    // last left, and the Views menu would call it edited on arrival (#6717 review). So the columns
    // are kept with it, and the tables on screen take them, as when it is opened from the menu.
    const starred = table?.views?.find((view) => view.isStarred && view.id === stored?.viewId);
    if (kept && starred && session) {
      const state: TableViewState = { ...fromTableViewStateJSON(starred.state), tabPath: kept.tabPath, search: kept.search };
      if (!isCurrentView(table?.currentView, starred.id, state)) {
        save(saveCurrentView, { session, tableKey, viewId: starred.id, state });
        openedView?.(tableKey);
      }
    }
    const target = kept ? hrefOf(kept) : defaultHref;
    if (target === undefined || isSameHref(target, here)) return;
    ownHref.current = target;
    void navigate(target, { replace: true, state: kept && stored ? { [OPENED_VIEW_STATE]: stored.viewId } : undefined });
  }, [
    answered,
    defaultHref,
    disabled,
    isBare,
    isLoggedIn,
    keepWhatStillExists,
    location,
    navigate,
    openedFromHistory,
    openedView,
    saveCurrentView,
    session,
    table,
    tableKey,
  ]);

  const storedViewId = table?.currentView?.viewId ?? null;
  const remember = useCallback(
    (href: string) => {
      if (disabled || !session?.isLoggedIn) return;
      ownHref.current = href;
      const url = new URL(href, "https://list.invalid");
      const view = listViewPathOf(url.pathname, url.search);
      const fromRef = openedViewId.current === undefined ? storedViewId : openedViewId.current;
      const viewId = openedFromHistory === undefined ? fromRef : openedFromHistory;
      // Once saved, the current view names the view it started from, and the cached answer says so.
      openedViewId.current = undefined;
      save(saveCurrentView, { session, tableKey, viewId, state: { tabPath: view.tabPath, search: view.search } });
    },
    [disabled, openedFromHistory, saveCurrentView, session, storedViewId, tableKey],
  );

  const defaultPath = new URL(defaultHref ?? basePath, "https://list.invalid").pathname;
  const keepsViews = isLoggedIn && !disabled;

  /**
   * Shows `view` -- a saved view, or null for the Default view -- and keeps it as the current view,
   * columns and all, so the list opens in it next time too (#6717). A saved view whose tab the page
   * no longer has opens the Default view's list, still named as the view the reader chose.
   */
  const openView = useCallback(
    (view: NamedTableViewJSON | null) => {
      if (!keepsViews || !session) return;
      const saved = view ? fromTableViewStateJSON(view.state) : undefined;
      const path = saved?.tabPath ? { tabPath: saved.tabPath, search: saved.search } : undefined;
      const kept = path && keepWhatStillExists ? keepWhatStillExists(path) : path;
      const target = kept ? hrefOf(kept) : defaultViewHref(defaultPath);
      const shown = kept ?? { tabPath: defaultPath, search: "" };
      const state: TableViewState = { ...(saved ?? DEFAULT_VIEW_COLUMNS), tabPath: shown.tabPath, search: shown.search };
      const viewId = view?.id ?? null;
      save(saveCurrentView, { session, tableKey, viewId, state });
      openedView?.(tableKey);
      ownHref.current = target;
      openedViewId.current = undefined;
      void navigate(target, { state: { [OPENED_VIEW_STATE]: viewId } });
    },
    [defaultPath, keepWhatStillExists, keepsViews, navigate, openedView, saveCurrentView, session, tableKey],
  );

  /**
   * Keeps what the list shows as the current view, starting from the saved view `viewId`: after the
   * reader saved it under that view, so the menu names it with nothing edited.
   */
  const keepAsCurrentView = useCallback(
    (viewId: string, state: TableViewState) => {
      if (!keepsViews || !session) return;
      openedViewId.current = undefined;
      save(saveCurrentView, { session, tableKey, viewId, state });
      // The page's own URL now, so the history entry names the view saved, not the one opened.
      ownHref.current = `${location.pathname}${location.search}`;
      void navigate(ownHref.current, { replace: true, state: { [OPENED_VIEW_STATE]: viewId } });
    },
    [keepsViews, location.pathname, location.search, navigate, saveCurrentView, session, tableKey],
  );

  const views = table?.views;
  const currentView = table?.currentView ?? undefined;
  const activeViewId = openedFromHistory === undefined ? storedViewId : openedFromHistory;
  const { tabPath: shownTabPath, search: shownSearch } = listViewPathOf(location.pathname, location.search);

  // One object while nothing in it changes: pages list it in the dependencies of the toolbar they
  // hand MRT, which would otherwise be built again on every render.
  return useMemo(
    () => ({
      /** Keeps `href`, the URL the reader's change is about to show, as the list's current view. */
      remember,
      /** The view the reader left the list in, as the account keeps it; undefined when they never changed it. */
      currentView,
      /** The saved view the current view started from; null for the page's Default view. */
      currentViewId: storedViewId,
      /** The reader's saved views, in their order, for the Views menu (#6717). */
      views: views ?? NO_VIEWS,
      /** The table's key on the account. */
      tableKey,
      /** Whether the reader keeps views here: signed in, and not shown one fixed view. */
      keepsViews,
      /** The path of the Default view's tab. */
      defaultPath,
      /** The saved view in use, or null for the Default view. A link shows against the reader's view. */
      activeViewId,
      /** What the list shows now, as a view keeps it. */
      shownView: { tabPath: shownTabPath, search: shownSearch } as ListViewPath,
      keepWhatStillExists,
      openView,
      keepAsCurrentView,
    }),
    [
      activeViewId,
      currentView,
      defaultPath,
      keepAsCurrentView,
      keepWhatStillExists,
      keepsViews,
      openView,
      remember,
      shownSearch,
      shownTabPath,
      storedViewId,
      tableKey,
      views,
    ],
  );
}

export type CurrentTableView = ReturnType<typeof useCurrentTableView>;

type SaveCurrentView = ReturnType<typeof useSaveCurrentTableViewMutation>[0];

/** At module level, so a failure's handling is plain code whatever the component compiles into. */
function save(saveCurrentView: SaveCurrentView, args: Parameters<SaveCurrentView>[0]): void {
  saveCurrentView(args)
    .unwrap()
    .catch((error: unknown) => {
      // The list still shows what the reader chose; it simply does not follow them elsewhere.
      // Nothing for them to do, so no snackbar.
      console.warn(`Could not keep the view of the ${args.tableKey} table.`, error);
    });
}

/** Whether the account's current view already is `state`, started from the saved view `viewId`. */
function isCurrentView(
  currentView: TableColumnPreferenceJSON["currentView"],
  viewId: string,
  state: TableViewState,
): boolean {
  if (currentView?.viewId !== viewId) return false;
  return JSON.stringify(fromTableViewStateJSON(currentView.state)) === JSON.stringify(state);
}

function isSameHref(href: string, location: { pathname: string; search: string }): boolean {
  const url = new URL(href, "https://list.invalid");
  return (
    url.pathname.replace(/\/+$/, "") === location.pathname.replace(/\/+$/, "") &&
    new URLSearchParams(url.search).toString() === new URLSearchParams(location.search).toString()
  );
}
